import React, { useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ViewStyle,
} from 'react-native';
import { NavigationProp } from '@react-navigation/native';
import { AdminErrorBoundary } from '@/components';
import { GradientBackground } from '@/components/shared/redesign';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { getScreenMetadata } from '@/utils/admin/navigation';

interface BaseAdminScreenProps {
  /** The current screen name for navigation highlighting */
  screenName: keyof AdminStackParamList;
  /** Navigation prop from React Navigation (kept for backward compatibility with callers) */
  navigation?: NavigationProp<AdminStackParamList>;
  /** Custom content to render in the main area */
  children?: React.ReactNode;
  /** Override the default title */
  title?: string;
  /** Override the default subtitle */
  subtitle?: string;
  /** Additional styles for the content container */
  contentContainerStyle?: ViewStyle;
  /** Whether to show the default placeholder content */
  showPlaceholder?: boolean;
  /** Whether scrolling is enabled (default: false) */
  scrollEnabled?: boolean;
}

export const BaseAdminScreen: React.FC<BaseAdminScreenProps> = React.memo(({
  screenName,
  children,
  title,
  subtitle,
  contentContainerStyle,
  showPlaceholder = true,
  scrollEnabled = false,
}) => {
  const { redesign } = useAdminColors();
  const { layoutMode } = useResponsive();
  const isDesktop = layoutMode === 'desktop';

  const screenMetadata = useMemo(() => getScreenMetadata(screenName), [screenName]);
  const displayTitle = title || screenMetadata.title;
  const displaySubtitle = subtitle || screenMetadata.subtitle;

  const placeholder = showPlaceholder ? (
    <View style={styles.placeholderContainer}>
      <Text style={[styles.title, { color: redesign.text.primary }]}>{displayTitle}</Text>
      <Text style={[styles.subtitle, { color: redesign.text.dim }]}>{displaySubtitle}</Text>
    </View>
  ) : null;

  const content = scrollEnabled ? (
    <ScrollView
      style={styles.content}
      contentContainerStyle={[styles.contentContainer, contentContainerStyle]}
      showsVerticalScrollIndicator={false}
      scrollEnabled={scrollEnabled}
    >
      {children || placeholder}
    </ScrollView>
  ) : (
    <View style={[styles.content, styles.contentContainer, contentContainerStyle]}>
      {children || placeholder}
    </View>
  );

  return (
    <AdminErrorBoundary>
      {isDesktop ? <GradientBackground>{content}</GradientBackground> : content}
    </AdminErrorBoundary>
  );
});

BaseAdminScreen.displayName = 'BaseAdminScreen';

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingTop: 40,
    paddingBottom: 40,
  },
  placeholderContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 32,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 16,
  },
  subtitle: {
    fontSize: 16,
    fontWeight: '400',
    textAlign: 'center',
  },
});
